"use client"

import { useState } from "react"
import type { TowerTypeConfig } from "@/types/tower.types"
import { TOWER_SPRITESHEET, TOWER_SPRITE_COUNT, TOWER_SPRITE_INDEX } from "@/components/game-ui/tower-sprite-index"
import { spriteStripStyle } from "@/components/game-ui/sprite-strip"

const getAttackSpeedLabel = (attackSpeed: number): string => {
  if (attackSpeed <= 30) return "Very Fast"
  if (attackSpeed <= 50) return "Fast"
  if (attackSpeed <= 80) return "Average"
  if (attackSpeed <= 120) return "Slow"
  return "Very Slow"
}

const getRangeLabel = (range: number): string => {
  if (range <= 150) return "Short"
  if (range <= 200) return "Average"
  if (range <= 250) return "Long"
  return "Very Long"
}

interface TowerDetailsProps {
  tower: TowerTypeConfig
}

export const TowerDetails = ({ tower }: TowerDetailsProps) => {
  const spriteIndex = TOWER_SPRITE_INDEX[tower.id]
  const hasSprite = spriteIndex !== undefined
  const [openLevel, setOpenLevel] = useState<number | null>(1)

  return (
    <div id="tower-details" className="space-y-4">
      {/* Large tower portrait */}
      <div className="flex justify-center">
        <div className="relative w-32 h-32 rounded-lg overflow-hidden bg-slate-800 ring-2 ring-amber-600/50">
          {hasSprite ? (
            <div
              className="w-full h-full"
              style={{
                backgroundImage: `url(${TOWER_SPRITESHEET})`,
                ...spriteStripStyle(spriteIndex, TOWER_SPRITE_COUNT),
              }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-4xl font-bold text-amber-400">
              ?
            </div>
          )}
        </div>
      </div>

      {/* Tower name */}
      <h2 className="text-2xl font-bold text-amber-300 text-center">
        {tower.name}
      </h2>

      {/* Description */}
      <p className="text-gray-300 text-sm text-center italic">
        {tower.description}
      </p>

      {/* Stats section with accordions */}
      <div className="space-y-2 pt-4 border-t border-amber-600/30">
        <h3 className="text-lg font-bold text-amber-400 mb-3">Stats by Level</h3>

        {tower.upgrades.map((upgrade) => {
          const isOpen = openLevel === upgrade.level

          return (
            <div
              key={upgrade.level}
              className="border border-amber-600/20 rounded-lg overflow-hidden bg-black/20"
            >
              {/* Accordion trigger */}
              <button
                onClick={() => setOpenLevel(isOpen ? null : upgrade.level)}
                className="w-full px-4 py-3 flex items-center justify-between hover:bg-amber-900/10 transition text-left"
              >
                <div className="flex items-center gap-3">
                  <span className="text-amber-400 font-bold text-base">
                    Level {upgrade.level}
                  </span>
                  <span className="text-green-400 font-bold text-sm">
                    ${upgrade.cost}
                  </span>
                </div>
                <div className="flex items-center gap-4 text-sm">
                  <span className="text-gray-400">
                    DMG: <span className="text-white font-bold">{upgrade.damage}</span>
                  </span>
                  <span className="text-gray-400">
                    RNG: <span className="text-white font-bold">{getRangeLabel(upgrade.range)}</span>
                  </span>
                  <svg
                    className={`w-5 h-5 text-amber-400 transition-transform ${
                      isOpen ? "rotate-180" : ""
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </button>

              {/* Accordion content */}
              {isOpen && (
                <div className="border-t border-amber-600/30 bg-gradient-to-b from-black/40 to-black/60">
                  <table className="w-full border-collapse">
                    <tbody>
                      <tr className="border-b border-amber-600/20">
                        <td className="py-3 px-4 text-gray-300 font-medium w-1/2">Damage</td>
                        <td className="py-3 px-4 text-white font-bold text-right text-lg">{upgrade.damage}</td>
                      </tr>
                      <tr className="border-b border-amber-600/20 bg-black/20">
                        <td className="py-3 px-4 text-gray-300 font-medium">Range</td>
                        <td className="py-3 px-4 text-right">
                          <div className="flex flex-col items-end gap-0.5">
                            <span className="text-white font-bold text-lg">{upgrade.range}</span>
                            <span className="text-gray-400 text-xs">{getRangeLabel(upgrade.range)}</span>
                          </div>
                        </td>
                      </tr>
                      <tr className="border-b border-amber-600/20">
                        <td className="py-3 px-4 text-gray-300 font-medium">Attack Speed</td>
                        <td className="py-3 px-4 text-right">
                          <div className="flex flex-col items-end gap-0.5">
                            <span className="text-white font-bold text-lg">{upgrade.attackSpeed}</span>
                            <span className="text-gray-400 text-xs">{getAttackSpeedLabel(upgrade.attackSpeed)}</span>
                          </div>
                        </td>
                      </tr>
                      <tr className="border-b border-amber-600/20 bg-black/20">
                        <td className="py-3 px-4 text-gray-300 font-medium">Max Health</td>
                        <td className="py-3 px-4 text-white font-bold text-right text-lg">{upgrade.maxHealth}</td>
                      </tr>
                      <tr className="bg-green-950/30">
                        <td className="py-3 px-4 text-gray-300 font-medium">Build Cost</td>
                        <td className="py-3 px-4 text-green-400 font-bold text-right text-lg">${upgrade.cost}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
